
import { useState, KeyboardEvent } from 'react';
import { Send, Paperclip, Mic, PanelLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { EmailModeToggle } from './email/EmailModeToggle';
import { EmailRichTextInput } from './email/EmailRichTextInput';
import { useEmailMode } from '@/hooks/chat/useEmailMode';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  onToggleSidebar?: () => void;
  onEmailSend?: (emailData: any) => void;
}

export function ChatInput({ onSendMessage, isLoading = false, disabled = false, onToggleSidebar, onEmailSend }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const {
    isEmailMode,
    emailDraft,
    toggleEmailMode,
    updateEmailDraft,
    resetEmailDraft,
    createEmailMessage
  } = useEmailMode();

  const handleSubmit = () => {
    if (isEmailMode) {
      handleEmailSend();
    } else if (message.trim() && !isLoading && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleEmailSend = () => {
    const emailMessage = createEmailMessage();
    if (emailMessage && onEmailSend) {
      onEmailSend(emailMessage);
      resetEmailDraft();
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSend = isEmailMode 
    ? emailDraft.to && emailDraft.subject && emailDraft.body
    : message.trim();

  return (
    <div className="bg-background">
      {/* Chat Input Container */}
      <div className="relative mx-4 mb-4">
        {isEmailMode ? (
          <EmailRichTextInput
            subject={emailDraft.subject}
            onSubjectChange={(subject) => updateEmailDraft({ subject })}
            to={emailDraft.to}
            onToChange={(to) => updateEmailDraft({ to })}
            content={emailDraft.body}
            onContentChange={(body) => updateEmailDraft({ body })}
            onSend={handleEmailSend}
            disabled={disabled || isLoading}
          />
        ) : (
          <>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask me anything..."
              disabled={disabled || isLoading}
              className="min-h-[3rem] max-h-32 resize-none border border-border/50 rounded-2xl bg-background px-4 pt-3 pb-12 text-sm placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-border pr-16"
            />
            
            {/* Toolbar */}
            <div className="absolute bottom-2 left-4 right-4 flex justify-between items-center">
              {/* Left side buttons */}
              <div className="flex items-center gap-1">
                {onToggleSidebar && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onToggleSidebar}
                    className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted/20 rounded-lg"
                    disabled={disabled}
                  >
                    <PanelLeft className="h-4 w-4" />
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted/20 rounded-lg"
                  disabled={disabled}
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted/20 rounded-lg"
                  disabled={disabled}
                >
                  <Mic className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Right side send button */}
              <Button
                onClick={handleSubmit}
                disabled={!canSend || isLoading || disabled}
                size="icon"
                className="h-8 w-8 rounded-lg bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}

        {/* Email Mode Toggle */}
        <div className="absolute top-2 right-2">
          <EmailModeToggle
            isEmailMode={isEmailMode}
            onToggle={toggleEmailMode}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}
